import {
  collection,
  doc,
  getDoc,
  getDocs,
  serverTimestamp,
  setDoc,
  type DocumentData,
} from 'firebase/firestore';
import type { AiVerification, AiVerificationAssignmentSet } from '@citizen-walk/shared-types';
import { db } from '../../lib/firebase';
import { mapAssignmentSet } from './useVerificationResults';

/**
 * 配分セット編集 (load / partial-update / status 切替) の Firestore helper
 * (Admin Wave AW4、docs/108 Critical #12,#13 / High #4,#5)。
 *
 * 認可: firestore.rules `match districts/{districtId}/aiVerificationAssignmentSets/{setId}` が
 *   `allow create, update: if isDistrictAdmin(districtId)` を既に許可しているため、
 *   status 切替・編集はクライアントからの直接 setDoc(merge) で実現する (専用 callable 不要)。
 *   delete のみ rules で禁止 (履歴・perUserAssignments 保護)。
 *
 * 旧 `old2/apps/admin/src/lib/verification.ts` の getAssignmentSet / saveAssignmentSet を踏襲しつつ、
 *   - generateAssignments callable で作られた set (perUserAssignments / generationConstraints を含む) を
 *     壊さないよう、編集は merge update で「変更したフィールドのみ」書き込む。
 *   - undefined キーは Firestore が拒否するため payload から除外。
 */

/** 1 件の配分セットを読み込む (mapAssignmentSet で正規化)。 */
export async function loadAssignmentSet(
  districtId: string,
  setId: string,
): Promise<AiVerificationAssignmentSet | null> {
  const ref = doc(db, 'districts', districtId, 'aiVerificationAssignmentSets', setId);
  const snap = await getDoc(ref);
  if (!snap.exists()) return null;
  return mapAssignmentSet({ id: snap.id, data: () => snap.data() as DocumentData });
}

/** 編集可能なフィールド (基本情報 + 期間 + status + 配分 + 候補 + session)。 */
export interface AssignmentSetEditPatch {
  displayName?: string;
  description?: string;
  templateId?: string;
  templateVersion?: number;
  distribution?: AiVerificationAssignmentSet['distribution'];
  candidatePostIds?: string[];
  randomConstraints?: AiVerificationAssignmentSet['randomConstraints'];
  sessionIds?: string[];
  sessionId?: string | null;
  startAt?: string | null;
  endAt?: string | null;
  status?: AiVerificationAssignmentSet['status'];
}

function stripUndefined(obj: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(obj)) {
    if (v !== undefined) out[k] = v;
  }
  return out;
}

/**
 * 配分セットを部分更新する (merge)。updatedAt / updatedBy を必ず付与。
 * 既存の perUserAssignments / generationConstraints / createdAt 等は touch しない。
 */
export async function saveAssignmentSetPatch(
  districtId: string,
  setId: string,
  patch: AssignmentSetEditPatch,
  actorUid: string,
): Promise<void> {
  const ref = doc(db, 'districts', districtId, 'aiVerificationAssignmentSets', setId);
  const payload = stripUndefined({
    ...patch,
    updatedAt: serverTimestamp(),
    updatedBy: actorUid,
  });
  await setDoc(ref, payload, { merge: true });
}

/**
 * status (draft / published / closed) のみを切替える (一覧の行アクション用)。
 * docs/108 Critical #12 — 「公開中にしないと参加者に表示されない」運用の要。
 */
export async function setAssignmentSetStatus(
  districtId: string,
  setId: string,
  status: AiVerificationAssignmentSet['status'],
  actorUid: string,
): Promise<void> {
  await saveAssignmentSetPatch(districtId, setId, { status }, actorUid);
}

/**
 * 1 投稿の verifications サブコレクションを全件取得する (結果個票モーダル用、Critical #14)。
 * admin は rules で全件 read 可 (`allow read: if isDistrictAdmin(districtId)`)。
 */
export async function fetchVerificationsForPost(
  districtId: string,
  postId: string,
): Promise<AiVerification[]> {
  const colRef = collection(db, 'districts', districtId, 'posts', postId, 'verifications');
  const snap = await getDocs(colRef);
  return snap.docs.map((d) => d.data() as AiVerification);
}
